<template>
  <form
    class="v3-form behaviour-create-core-value-form"
    :data-testid="
      isEditMode
        ? 'behaviour-edit-core-value-form'
        : 'behaviour-create-core-value-form'
    "
    @submit.prevent="onSubmit"
  >
    <fieldset class="behaviour-core-value-status-field mb-3 border-0 p-0 m-0">
      <legend
        id="behaviour-cv-status-legend"
        class="v3-form-label required d-block mb-2"
      >
        {{ fieldLabels.status }}
      </legend>
      <div
        class="behaviour-core-value-status-radios d-flex flex-wrap gap-4"
        role="radiogroup"
        aria-labelledby="behaviour-cv-status-legend"
      >
        <div
          v-for="option in statusOptions"
          :key="option.value"
          class="behaviour-core-value-status-radio d-flex align-items-center gap-2"
        >
          <input
            :id="`behaviour-cv-status-${option.value}`"
            type="radio"
            name="behaviour-cv-status"
            class="behaviour-core-value-status-radio__input"
            :value="option.value"
            :checked="Number(state.form.status) === Number(option.value)"
            :data-testid="`behaviour-cv-status-${option.value}`"
            @change="onStatusChange($event.target.value)"
          />
          <label
            :for="`behaviour-cv-status-${option.value}`"
            class="behaviour-core-value-status-radio__label mb-0"
          >
            {{ option.label }}
          </label>
        </div>
      </div>
    </fieldset>

    <div class="row mb-3 g-3 behaviour-create-core-value-form__name-color-row">
      <div class="col-12 col-md-6 behaviour-create-core-value-form__field">
        <Label
          required
          label-id="behaviour-cv-name-lbl"
          html-for="behaviour-cv-name"
        >
          {{ fieldLabels.name }}
        </Label>
        <InputField
          id="behaviour-cv-name"
          label-id="behaviour-cv-name-lbl"
          class="w-100"
          :model-value="state.form.name"
          :invalid-feedback="errors.name"
          :max-length="String(maxNameLength)"
          data-testid="behaviour-cv-name-input"
          @update:model-value="(v) => patchForm('name', v)"
        />
      </div>

      <div class="col-12 col-md-6 behaviour-create-core-value-form__field">
        <Label
          required
          label-id="behaviour-cv-color-lbl"
          html-for="behaviour-cv-color-trigger"
        >
          {{ fieldLabels.color }}
        </Label>
        <ColorPicker
          trigger-input-id="behaviour-cv-color-trigger"
          class="w-100 behaviour-create-core-value-form__color-picker"
          :palette="coreValueColorPalette"
          :palette-columns="4"
          :model-value="state.form.category_color"
          :invalid-feedback="errors.category_color"
          data-testid="behaviour-cv-color-picker"
          @update:model-value="onColorChange"
        />
      </div>
    </div>

    <div class="mb-3">
      <Label
        label-id="behaviour-cv-description-lbl"
        html-for="behaviour-cv-description"
      >
        {{ fieldLabels.description }}
      </Label>
      <InputField
        id="behaviour-cv-description"
        label-id="behaviour-cv-description-lbl"
        class="w-100"
        type="textarea"
        :rows="4"
        :model-value="state.form.description"
        :invalid-feedback="errors.description"
        :max-length="String(maxDescriptionLength)"
        data-testid="behaviour-cv-description-input"
        @update:model-value="(v) => patchForm('description', v)"
      />
    </div>

    <InfoBox
      v-if="state.serverMessage"
      class="mb-3"
      :class="{ danger: state.isServerError }"
      :title="state.serverTitle"
      role="status"
      aria-live="polite"
    >
      {{ state.serverMessage }}
    </InfoBox>

    <div class="text-center">
      <LoadingButton
        class="me-2 px-4"
        type="button"
        rounded
        data-testid="behaviour-cv-cancel"
        @click="onCancel"
      >
        {{ $t("common.cancel") }}
      </LoadingButton>
      <LoadingButton
        class="px-4"
        type="submit"
        solid
        rounded
        :is-loading="state.isProcessing"
        :disabled="state.isProcessing"
        :data-testid="
          isEditMode ? 'behaviour-cv-update' : 'behaviour-cv-submit'
        "
      >
        {{ submitButtonLabel }}
      </LoadingButton>
    </div>
  </form>
</template>

<script>
import {
  coreValueRowToForm,
  createEmptyCoreValueForm,
  parseCoreValueApiError,
  validateCoreValueForm
} from "@/helpers/behaviourCoreValueForm.js"
import InfoBox from "@/v3components/shared/Alerts/InfoBox.vue"
import LoadingButton from "@/v3components/shared/Buttons/LoadingButton.vue"
import ColorPicker from "@/v3components/shared/Form/ColorPicker.vue"
import behaviourCoreValueConstants from "@/constants/behaviourCoreValueConstants.js"
import colorPickerPaletteConstants from "@/constants/colorPickerPaletteConstants.js"
import InputField from "@/v3components/shared/Form/InputField.vue"
import Label from "@/v3components/shared/Form/Label.vue"
import { computed, inject, onMounted, reactive } from "vue"
import { useI18n } from "vue-i18n"
import { useStore } from "vuex"

export default {
  name: "BehaviourCreateCoreValueForm",
  components: {
    ColorPicker,
    InfoBox,
    InputField,
    Label,
    LoadingButton
  },
  props: {
    existingNames: {
      type: Array,
      default: () => []
    },
    editableCoreValue: {
      type: Object,
      default: null
    }
  },
  emits: ["cancel"],
  setup(props, { emit }) {
    const { t } = useI18n()
    const store = useStore()
    const modal = inject("modal")

    const maxNameLength = behaviourCoreValueConstants.MAX_NAME_LENGTH
    const maxDescriptionLength =
      behaviourCoreValueConstants.MAX_DESCRIPTION_LENGTH
    const coreValueColorPalette =
      behaviourCoreValueConstants.CORE_VALUE_COLOR_PALETTE

    const behaviourTerminology = computed(
      () => store.getters["behaviourSettings/behaviourTerminology"]
    )

    const isEditMode = computed(() => Boolean(props.editableCoreValue?.id))

    const submitButtonLabel = computed(() =>
      isEditMode.value
        ? t("behaviour_settings.update_core_value_submit")
        : t("behaviour_settings.create_core_value_submit")
    )

    const fieldLabels = computed(() => {
      const bt = behaviourTerminology.value
      return {
        name: t("behaviour_settings.core_value_name_label", {
          coreValue: bt.coreValue
        }),
        description: t("behaviour_settings.core_value_description_label"),
        color: t("behaviour_settings.core_value_color_label"),
        status: t("behaviour_settings.core_value_status_label")
      }
    })

    const statusOptions = computed(() => [
      {
        value: 1,
        label: behaviourCoreValueConstants.STATUS.ENABLED
      },
      {
        value: 0,
        label: behaviourCoreValueConstants.STATUS.DISABLED
      }
    ])

    const state = reactive({
      form: createEmptyCoreValueForm(),
      isProcessing: false,
      serverTitle: "",
      serverMessage: "",
      isServerError: false,
      touched: false,
      apiFieldErrors: {}
    })

    const validationErrors = computed(() => {
      if (!state.touched) return {}
      const clientErrors = validateCoreValueForm(
        state.form,
        isEditMode.value ? props.existingNames : [],
        {
          excludeName: props.editableCoreValue?.name
        }
      )
      return { ...clientErrors, ...state.apiFieldErrors }
    })

    const fieldErrorLabels = computed(() => ({
      name: fieldLabels.value.name,
      category_color: fieldLabels.value.color,
      status: fieldLabels.value.status,
      description: fieldLabels.value.description
    }))

    const errorMessages = computed(() => ({
      maxLength: t("behaviour_settings.core_value_error_max_length", {
        max: maxNameLength
      }),
      duplicate: t("behaviour_settings.core_value_error_duplicate", {
        coreValue: behaviourTerminology.value.coreValue
      }),
      invalidColor: t("behaviour_settings.core_value_error_invalid_color")
    }))

    const errors = computed(() => {
      const raw = validationErrors.value
      const messages = errorMessages.value
      const labels = fieldErrorLabels.value
      const out = {}
      Object.keys(raw).forEach((key) => {
        const code = raw[key]
        if (key === "description" && code === "maxLength") {
          out[key] = t("behaviour_settings.core_value_error_max_length", {
            max: maxDescriptionLength
          })
        } else if (key === "category_color" && code === "invalidColor") {
          out[key] = messages.invalidColor
        } else if (code === "required") {
          out[key] = t("behaviour_settings.core_value_error_required_field", {
            field: labels[key] || key
          })
        } else if (code === "duplicate") {
          out[key] = messages.duplicate
        } else if (code === "maxLength") {
          out[key] = messages.maxLength
        } else {
          out[key] = t("behaviour_settings.core_value_error_required_field", {
            field: labels[key] || key
          })
        }
      })
      return out
    })

    const patchForm = (key, value) => {
      if (!key) return
      state.form[key] = value ?? ""
      state.serverMessage = ""
      state.isServerError = false
      if (state.apiFieldErrors[key]) {
        const next = { ...state.apiFieldErrors }
        delete next[key]
        state.apiFieldErrors = next
      }
    }

    const onStatusChange = (value) => {
      const parsed = Number(value)
      patchForm("status", Number.isNaN(parsed) ? value : parsed)
    }

    const onColorChange = (value) => {
      const hex = String(value ?? "").trim()
      patchForm("category_color", hex)
      if (hex.startsWith("#")) {
        state.form.category =
          colorPickerPaletteConstants.resolveCoreValueCategoryCode(hex) || hex
      }
    }

    onMounted(() => {
      if (props.editableCoreValue) {
        Object.assign(state.form, coreValueRowToForm(props.editableCoreValue))
      }
    })

    const onCancel = () => {
      emit("cancel")
    }

    const onSubmit = async () => {
      state.touched = true
      state.apiFieldErrors = {}
      const fieldErrors = validateCoreValueForm(
        state.form,
        isEditMode.value ? props.existingNames : [],
        {
          excludeName: props.editableCoreValue?.name
        }
      )
      if (Object.keys(fieldErrors).length) return

      try {
        state.isProcessing = true
        state.isServerError = false
        state.serverTitle = ""

        const coreValueTerm = behaviourTerminology.value.coreValue

        if (isEditMode.value) {
          await store.dispatch("behaviourSettings/updateCoreValue", {
            id: props.editableCoreValue.id,
            form: state.form,
            existingRow: props.editableCoreValue
          })
          state.serverMessage = t(
            "behaviour_settings.core_value_updated_success",
            { coreValue: coreValueTerm }
          )
          modal?.setAction?.("coreValueUpdated", {
            id: props.editableCoreValue.id,
            form: { ...state.form }
          })
        } else {
          const { id } = await store.dispatch(
            "behaviourSettings/createCoreValue",
            state.form
          )
          state.serverMessage = t(
            "behaviour_settings.core_value_created_success",
            { coreValue: coreValueTerm }
          )
          modal?.setAction?.("coreValueCreated", {
            ...state.form,
            id
          })
        }

        setTimeout(() => {
          emit("cancel")
        }, 600)
      } catch (error) {
        const parsed = parseCoreValueApiError(error)
        if (parsed.field === "name" && parsed.code === "duplicate") {
          state.apiFieldErrors = { name: "duplicate" }
          state.isServerError = false
          state.serverTitle = ""
          state.serverMessage = ""
          return
        }

        const coreValueTerm = behaviourTerminology.value.coreValue
        state.isServerError = true
        state.serverTitle = t("behaviour_settings.core_value_error_title")
        state.serverMessage =
          parsed.message ||
          (isEditMode.value
            ? t("behaviour_settings.core_value_updated_error", {
                coreValue: coreValueTerm
              })
            : t("behaviour_settings.core_value_created_error", {
                coreValue: coreValueTerm
              }))
      } finally {
        state.isProcessing = false
      }
    }

    return {
      state,
      errors,
      isEditMode,
      submitButtonLabel,
      fieldLabels,
      statusOptions,
      coreValueColorPalette,
      onStatusChange,
      onColorChange,
      maxNameLength,
      maxDescriptionLength,
      patchForm,
      onCancel,
      onSubmit
    }
  }
}
</script>
